import { FC, useEffect, useMemo, useRef, useState, CSSProperties, Key, } from "react";
// 组件
import { ProTooltip } from "../antd";
import SelectInput from "./subpage/selectInput";
import SelectTags from "./subpage/selectTags";
import SelectTabDropdown from "./subpage/dropdown";
// 方法
import classNames from "classnames";
import useClickOutside from "../Hooks/useClickOutside";
import { getElementScrollLeftAndTop, getElementTopAndLeft } from "../utils/utils";
import { formatOptionsAndInitOptions, getSelectTabOptions, paramsFilterOptions } from "./util";
// 常量
import { DEFAULT_FIELD_NAMES, DropdownOffset, SelectTabOption, SelectTabProps, SelectTabValue } from "./interface";
import { SUCCESS_STATUS_CODE, TABLE_COLUMN_EMPTY } from "../constants/constant";
import { API_STATUS_ENUM } from "../constants/enum";
import './style.less';

/** 分tabs下拉选择框 */
const SelectTab: FC<SelectTabProps> = props => {
  const {
    options: outOptions, loading: outLoading, value: outValue, allowClear, showSearch, selectTabKey, isDisabledEllipsis,
    changeEmptyValue, style, className, disabled, otherParams, dataKey, placeholder, width, isMultiple, defaultValue,
    listHeight, searchKey, showTab, zIndex, tabItems, fieldNames, dropdownStyle, dropdownClassName, isLocalSearch,
    searchPlaceholder, columnNumber, dropdownMatchSelectWidth, optionsEmptyTip, searchOptionsEmptyTip, loadingType,
    searchDiscriminateCase, isSelectAll, isReverseSelection, isGroupSelectAll, isGroupReverseSelection, isCacheHasParams,
    disabledRender, localSearchExcludeFieldname, initOptions, fetchApi, sort, onBlur, onFocus, getPopupContainer,
    onClear, onChange, onSelect, onBefore, formatOptions, onFetchcomplete, tabsChangeFormatOption,
  } = props;
  const [value, setValue] = useState<SelectTabValue | undefined>(defaultValue); // 下拉框选中的值
  const [loading, setLoading] = useState<boolean>(false); // 是否处于加载中状态
  const [isFocus, setIsFocus] = useState<boolean>(false); // 下拉框是否获取焦点
  const [options, setOptions] = useState<SelectTabOption[]>(); // 选项
  const wrapperRef = useRef<HTMLDivElement>(null); // 下拉框节点
  const dropdownRef = useRef<HTMLDivElement>(null); // dropdown节点
  const isFirst = useRef<boolean>(false); // 是否已经打开过下拉框
  const dropdownOffset = useRef<DropdownOffset>(); // dropdown定位数据
  const dropdownWidth = useRef<number>(); // dropdown宽度
  const isChange = useRef<boolean>(false); // 是否触发onChange回调
  const timer = useRef<any>(); // 获取缓存数据定时器
  const initOptionsStringify = JSON.stringify(initOptions || []);
  const otherParamsString = otherParams ? JSON.stringify(otherParams) : '';
  const prevOptions = useRef<SelectTabOption[]>();
  useClickOutside([wrapperRef, dropdownRef], isFocus, () => {
    setIsFocus(false);
    onBlur?.();
  });
  useEffect(() => {
    isChange.current = false;
    if (isMultiple && typeof outValue === 'string') {
      setValue(undefined);
      return;
    }
    if (!isMultiple && Array.isArray(outValue)) {
      setValue(undefined);
      return;
    }
    setValue(outValue || defaultValue);
  }, [outValue]);
  useEffect(() => {
    isChange.current && onChange?.(value, getSelectTabOptions(value, options, fieldNames));
  }, [value]);
  useEffect(() => {
    if (loadingType === 'init') {
      changeEmptyValue && setValue(undefined);
      getOptions();
    }
  }, [outOptions, fetchApi, otherParamsString, loadingType]);
  useEffect(() => {
    if (Array.isArray(initOptions) && initOptions.length) {
      const currentOptions = prevOptions.current;
      if (Array.isArray(currentOptions) && currentOptions.length) {
        const newOptions = currentOptions.slice();
        initOptions.forEach(item => {
          const valueKey = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
          const findItem = newOptions?.find(v => v[valueKey] === item[valueKey]);
          if (!findItem) {
            newOptions?.unshift(item);
          }
        });
        handleSetOptions(newOptions);
        return;
      }
      handleSetOptions(initOptions);
    }
  }, [initOptionsStringify]);
  /** 点击下拉框 */
  const handleClickWrapper = () => {
    if (disabled) return;
    handleSetOffset();
    setIsFocus(prev => {
      const newPrev = !prev;
      if (newPrev && !isFirst.current) {
        isFirst.current = true;
      }
      if (newPrev) {
        if (loadingType === 'open') {
          getOptions();
        }
        onFocus?.();
      } else {
        onBlur?.();
      }
      return newPrev;
    });
  };
  /** 清除选中项 */
  const handleClear = () => {
    isChange.current = true;
    setValue(undefined);
    onClear?.();
  };
  /** 获取数据 */
  const getOptions = async (searchValue?: string) => {
    if (outOptions) {
      handleSetOptions(outOptions);
      return;
    }
    if (fetchApi) {
      if (selectTabKey) {
        const CACHE_OPTIONS = window.sessionStorage.getItem(selectTabKey);
        if (CACHE_OPTIONS) {
          if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
            timer.current && clearInterval(timer.current);
            timer.current = setInterval(() => {
              const resData = window.sessionStorage.getItem(selectTabKey);
              if (resData && resData !== API_STATUS_ENUM.LOADING) {
                clearInterval(timer.current);
                try {
                  const data = formatOptionsAndInitOptions(fieldNames?.value, JSON.parse(resData), initOptions);
                  const formatData = onFetchcomplete ? onFetchcomplete(data) : data;
                  handleSetOptions(formatData || []);
                } catch (errorInfo) {
                  handleSetOptions([])
                }
              }
            }, 300);
          } else {
            try {
              const data = formatOptionsAndInitOptions(fieldNames?.value, JSON.parse(CACHE_OPTIONS), initOptions);
              const formatData = onFetchcomplete ? onFetchcomplete(data) : data;
              handleSetOptions(paramsFilterOptions(formatData, otherParams, localSearchExcludeFieldname) || []);
            } catch (errorInfo) {
              handleSetOptions([]);
            }
          }
          return;
        } else {
          window.sessionStorage.setItem(selectTabKey, API_STATUS_ENUM.LOADING);
        }
      }
      let params: any = otherParams;
      if (searchValue) {
        params[searchKey || fieldNames?.label || DEFAULT_FIELD_NAMES.label] = searchValue;
      }
      if (onBefore) {
        params = onBefore(params);
        if (params === false) {
          if (selectTabKey) {
            const CACHE_OPTIONS = window.sessionStorage.getItem(selectTabKey);
            if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
              window.sessionStorage.removeItem(selectTabKey);
            }
          }
          return;
        }
      }
      setLoading(true);
      const res = await fetchApi(selectTabKey ? (isCacheHasParams ? params : {}) : params);
      setLoading(false);
      if (res.code === SUCCESS_STATUS_CODE) {
        const data = (dataKey ? res.data?.[dataKey] : res.data) || [];
        // 数据存入缓存
        if (selectTabKey) {
          window.sessionStorage.setItem(
            selectTabKey,
            JSON.stringify(data || []),
          );
        }
        const newData = formatOptionsAndInitOptions(fieldNames?.value, data, initOptions);
        const formatData = onFetchcomplete ? onFetchcomplete(newData) : newData;
        if (selectTabKey) {
          handleSetOptions(paramsFilterOptions(formatData, otherParams, localSearchExcludeFieldname) || []);
        } else {
          handleSetOptions(formatData || []);
        }
      }
    }
  }
  /** 设置options值，并存储到ref */
  const handleSetOptions = (newOptions: SelectTabOption[]) => {
    prevOptions.current = newOptions;
    setOptions(newOptions);
  };
  /** 获取下拉框定位 */
  const handleSetOffset = () => {
    const selectElement = wrapperRef.current;
    const popupContainerOffset: DropdownOffset = {left: 0, top: 0};
    if (getPopupContainer) {
      const popupContainer: HTMLElement = getPopupContainer();
      const popupOffset = getElementTopAndLeft(popupContainer);
      popupContainerOffset.left = popupOffset.left;
      popupContainerOffset.top = popupOffset.top;
    }
    if (selectElement) {
      const selectHeight = selectElement.clientHeight;
      const selectWidth = selectElement.clientWidth;
      const selectOffset = getElementTopAndLeft(selectElement);
      const selectScroll = getElementScrollLeftAndTop(selectElement);
      const pageHeight = document.body.scrollHeight;
      const pageWidth = document.body.scrollWidth;
      dropdownWidth.current = Math.max(selectElement.clientWidth, dropdownMatchSelectWidth || 0);
      let dropdownLeft = selectOffset.left - popupContainerOffset.left - selectScroll.left;
      let dropdownTop = selectOffset.top + selectHeight - popupContainerOffset.top - selectScroll.top;
      let dropdownBottom = 0;
      if ((dropdownLeft + dropdownWidth.current) > pageWidth) {
        dropdownLeft = dropdownLeft - dropdownWidth.current + selectWidth;
      }
      if ((dropdownTop + (listHeight || 350)) > pageHeight) {
        dropdownBottom = pageHeight - dropdownTop + selectHeight;
        dropdownTop = 0;
      }
      dropdownOffset.current = {
        left: dropdownLeft,
        top: dropdownTop,
        bottom: dropdownBottom,
      };
    }
  };
  /** 点击选项 */
  const handleClickOption = (optionValue?: Key) => {
    isChange.current = true;
    if (!optionValue) return;
    if (isMultiple) {
      const newValue = new Set(value as Key[]);
      if (newValue.has(optionValue)) {
        newValue.delete(optionValue);
      } else {
        newValue.add(optionValue);
      }
      const nextValue = newValue.size ? [...newValue] : undefined;
      setValue(nextValue);
      onSelect?.(nextValue, getSelectTabOptions(nextValue, options, fieldNames));
      return;
    }
    if (value !== optionValue) {
      setValue(optionValue);
      onSelect?.(optionValue, getSelectTabOptions(optionValue, options, fieldNames))
    }
    setIsFocus(false);
    onBlur?.();
  };
  /** 全选 */
  const handleClickSelectAll = (groupOptionValues?: Key[]) => {
    if (!groupOptionValues || !isMultiple || !isSelectAll) return;
    isChange.current = true;
    const newValue = new Set(value as Key[]);
    groupOptionValues.forEach(optionValue => {
      if (!newValue.has(optionValue)) {
        newValue.add(optionValue);
      }
    });
    const nextValue = newValue.size ? [...newValue] : undefined;
    setValue(nextValue);
    onSelect?.(nextValue, getSelectTabOptions(nextValue, options, fieldNames));
  };
  /** 反选 */
  const handleReverseSelect = (groupOptionValues?: Key[]) => {
    if (!groupOptionValues || !isMultiple || !isReverseSelection) return;
    isChange.current = true;
    const newValue = new Set(value as Key[]);
    groupOptionValues.forEach(optionValue => {
      if (newValue.has(optionValue)) {
        newValue.delete(optionValue);
      } else {
        newValue.add(optionValue);
      }
    });
    const nextValue = newValue.size ? [...newValue] : undefined;
    setValue(nextValue);
    onSelect?.(nextValue, getSelectTabOptions(nextValue, options, fieldNames));
  };
  /** 远程搜索 */
  const handleSearch = (searchValue?: string) => {
    getOptions(searchValue)
  };
  /** 禁用显示 */
  const detailRender = () => {
    if (disabledRender !== undefined) return disabledRender;
    const disabledRenderNode = getSelectTabOptions(value, options, fieldNames);
    if (Array.isArray(disabledRenderNode)) {
      const joinRender = disabledRenderNode.map(v => v[fieldNames?.label || DEFAULT_FIELD_NAMES.label]).join('，');
      return isDisabledEllipsis ? (
        <ProTooltip title={joinRender} placement="topLeft">{joinRender}</ProTooltip>
      ) : joinRender;
    }
    return TABLE_COLUMN_EMPTY;
  };
  const dropdownOptions = useMemo(() => {
    if (formatOptions) return formatOptions(options);
    return options;
  }, [options, formatOptions]);
  const wrapperClasses = useMemo(() => {
    return classNames('yb-select-tab', className, {
      'yb-select-tab-focus': isFocus,
      'yb-select-tab-disabled': disabled,
    });
  }, [className, disabled, isFocus]);
  const wrapperStyle: CSSProperties = useMemo(() => {
    const newWrapperStyle: CSSProperties = {...style};
    if (width !== undefined) newWrapperStyle.width = width;
    return newWrapperStyle;
  }, [width, style]);

  return (
    <>
      {
        disabled ? (
          <div className={isDisabledEllipsis ? 'yb-line-ellipsis-1' : undefined} style={{ borderBottom: 'dashed 1px #adb5bd' }}>
            {detailRender()}
          </div>
        ) : (
          <>
            <div
              ref={wrapperRef}
              style={wrapperStyle}
              className={wrapperClasses}
              onClick={handleClickWrapper}
            >
              <SelectTags
                value={value}
                options={options}
                fieldNames={fieldNames}
              />
              <SelectInput
                placeholder={placeholder}
                loading={outLoading || loading}
                value={value}
                disabled={disabled}
                allowClear={allowClear}
                onClear={handleClear}
              />
            </div>
            {
              isFirst.current && (
                <SelectTabDropdown
                  dropdownRef={dropdownRef}
                  value={value}
                  show={isFocus}
                  allowClear={allowClear}
                  left={dropdownOffset.current?.left}
                  top={dropdownOffset.current?.top}
                  bottom={dropdownOffset.current?.bottom}
                  listHeight={listHeight}
                  options={dropdownOptions}
                  tabItems={tabItems}
                  zIndex={zIndex}
                  fieldNames={fieldNames}
                  dropdownStyle={dropdownStyle}
                  dropdownClassName={dropdownClassName}
                  isLocalSearch={isLocalSearch}
                  searchPlaceholder={searchPlaceholder}
                  columnNumber={columnNumber}
                  isMultiple={isMultiple}
                  showSearch={showSearch}
                  optionsEmptyTip={optionsEmptyTip}
                  searchOptionsEmptyTip={searchOptionsEmptyTip}
                  searchDiscriminateCase={searchDiscriminateCase}
                  isSelectAll={isSelectAll}
                  isReverseSelection={isReverseSelection}
                  isGroupSelectAll={isGroupSelectAll}
                  isGroupReverseSelection={isGroupReverseSelection}
                  showTab={showTab}
                  width={dropdownWidth.current}
                  sort={sort}
                  getPopupContainer={getPopupContainer}
                  onClickOption={handleClickOption}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  onClickSelectAll={handleClickSelectAll}
                  onReverseSelect={handleReverseSelect}
                  tabsChangeFormatOption={tabsChangeFormatOption}
                />
              )
            }
          </>
        )
      }
    </>
  );
};

SelectTab.defaultProps = {
  placeholder: '搜索内容',
  fieldNames: DEFAULT_FIELD_NAMES,
  isMultiple: false,
  listHeight: 350,
  isLocalSearch: true,
  allowClear: true,
  showSearch: true,
  isDisabledEllipsis: false,
  searchDiscriminateCase: false,
  isSelectAll: true,
  isReverseSelection: true,
  isGroupSelectAll: true,
  isGroupReverseSelection: false,
  showTab: true,
  changeEmptyValue: false,
  isCacheHasParams: false,
  loadingType: 'init',
};

export default SelectTab;
